import React from 'react';
import Box from '@mui/material/Box';
import { sp } from '@/theme/mui';
import { useLabel } from 'react-aria';
import Tooltip from '@/components/atoms/Tooltip/Tooltip';
import Label from '@/components/atoms/Label/Label';
import SupportingText from '@/components/atoms/SupportingText/SupportingText';
import { Icon } from '@/components/atoms/Icon/Icon';

export const BaseField = ({
  id = 'form-field',
  ariaLabel,
  label,
  tooltip,
  description,
  isRequired,
  isHideTitle,
  children,
  className,
  ...props
}: BaseFieldProps) => {
  const { labelProps } = useLabel({
    ...props,
    id,
    'aria-label': ariaLabel || label,
  });

  return (
    <Box className={className} sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {label && !isHideTitle && (
          <Box sx={{ mb: sp[4] }}>
            <Label
              id={id}
              text={label}
              {...labelProps}
              isRequired={isRequired}
              aria-labelledby={id}
            />
          </Box>
        )}
        {tooltip && !isHideTitle && (
          <Tooltip content={tooltip}>
            <Box sx={{ color: '#8c8c8d' }}>
              <Icon icon='question-circle' fontSize={16} />
            </Box>
          </Tooltip>
        )}
      </Box>
      {children}
      {description && (
        <Box sx={{ mt: sp[4] }}>
          <SupportingText text={description} />
        </Box>
      )}
    </Box>
  );
};

export interface FieldProps<T> {
  id?: string;
  ariaLabel?: string;
  label?: string;
  description?: string;
  placeholder?: string;
  tooltip?: string;
  value?: T;
  onChange?: (value: T) => void;
  validate?: (value?: T) => boolean;
  isRequired?: boolean;
  isHideTitle?: boolean;
  isDisabled?: boolean;
  showError?: (msg: string) => void;
}

export interface BaseFieldProps {
  id?: string;
  ariaLabel?: string;
  label?: string;
  tooltip?: string;
  description?: string;
  isRequired?: boolean;
  isHideTitle?: boolean;
  isDisabled?: boolean;
  children?: React.ReactNode;
  validate?: (value?: string | boolean) => boolean;
  className?: string;
}

BaseField.componentName = 'BaseField';

export default BaseField;
